import React, { useEffect, useState } from "react";
import "../Style/AllCoursesDetails.css";
import judicialLogo from "../Images/judicialLogo.png";
import { useDispatch, useSelector } from "react-redux";
import { getCoursesDetail } from "../Redux/Auth/AuthSlice";
import Spinner from "../Spinner/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faCopy,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const AllCoursesDetails = () => {
  const dispatch = useDispatch();
  const selector = useSelector((s) => s.AuthSlice);
  const getCoursesDetails = selector.getCoursesDetails || [];
  const loader = selector.loader;

  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [lessonPages, setLessonPages] = useState({});
  const [subjectPages, setSubjectPages] = useState({});
  const [videoPages, setVideoPages] = useState({});
  const [copiedVideoId, setCopiedVideoId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);

  // useEffect(() => {
  //   dispatch(getCoursesDetail());
  // }, [dispatch]);

  // Filter courses based on search query and dropdown selections
  useEffect(() => {
    let filtered = getCoursesDetails;

    // Apply search query filtering
    if (searchQuery.trim()) {
      filtered = getCoursesDetails
        .map((course) => {
          const filteredLessons = course.lessons
            .map((lesson) => {
              const filteredSubjects = lesson.subjects
                .map((subject) => {
                  const filteredVideos = subject.videos.filter((video) =>
                    video.name.toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  return filteredVideos.length > 0
                    ? { ...subject, videos: filteredVideos }
                    : null;
                })
                .filter((subject) => subject !== null);
              return filteredSubjects.length > 0
                ? { ...lesson, subjects: filteredSubjects }
                : null;
            })
            .filter((lesson) => lesson !== null);
          return filteredLessons.length > 0
            ? { ...course, lessons: filteredLessons }
            : null;
        })
        .filter((course) => course !== null);
    }

    // Apply dropdown-based filtering
    if (selectedCourse) {
      filtered = filtered.filter((course) => course.courseId === selectedCourse.courseId);
      if (selectedLesson) {
        filtered = filtered.map((course) => ({
          ...course,
          lessons: course.lessons.filter((lesson) => lesson.lessonId === selectedLesson.lessonId),
        }));
        if (selectedSubject) {
          filtered = filtered.map((course) => ({
            ...course,
            lessons: course.lessons.map((lesson) => ({
              ...lesson,
              subjects: lesson.subjects.filter(
                (subject) => subject.subjectId === selectedSubject.subjectId
              ),
            })),
          }));
        }
      }
    }

    // Auto-expand filtered results only when search or dropdowns are used
    const newExpandedCourses = {};
    const newExpandedLessons = {};
    const newExpandedSubjects = {};

    if (searchQuery.trim() || selectedCourse) {
      filtered.forEach((course) => {
        newExpandedCourses[course.courseId] = true;
        course.lessons.forEach((lesson) => {
          newExpandedLessons[lesson.lessonId] = true;
          lesson.subjects.forEach((subject) => {
            newExpandedSubjects[subject.subjectId] = true;
          });
        });
      });
    }

    setExpandedCourses(newExpandedCourses);
    setExpandedLessons(newExpandedLessons);
    setExpandedSubjects(newExpandedSubjects);
    setFilteredCourses(filtered.length > 0 ? filtered : getCoursesDetails);
  }, [searchQuery, getCoursesDetails, selectedCourse, selectedLesson, selectedSubject]);

  // Calculate video counts
  const getSubjectVideoCount = (subject) => subject.videos.length;
  const getLessonVideoCount = (lesson) =>
    lesson.subjects.reduce((total, subject) => total + getSubjectVideoCount(subject), 0);
  const getCourseVideoCount = (course) =>
    course.lessons.reduce((total, lesson) => total + getLessonVideoCount(lesson), 0);

  // Handle pagination
  const handleSubPagination = (type, id, direction) => {
    if (type === "lesson") {
      setLessonPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    } else if (type === "subject") {
      setSubjectPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    } else if (type === "video") {
      setVideoPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    }
  };

  // Toggle expand/collapse
  const toggleExpand = (type, id) => {
    if (type === "course") {
      setExpandedCourses((prev) => ({ ...prev, [id]: !prev[id] }));
    } else if (type === "lesson") {
      setExpandedLessons((prev) => ({ ...prev, [id]: !prev[id] }));
    } else if (type === "subject") {
      setExpandedSubjects((prev) => ({ ...prev, [id]: !prev[id] }));
    }
  };

  // Handle copy link
  const handleCopyLink = (videoId, url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedVideoId(videoId);
      setTimeout(() => setCopiedVideoId(null), 2000);
    }).catch((err) => {
      console.error("Failed to copy: ", err);
    });
  };

  // Handle dropdown selection
  const handleDropdownChange = (type, value) => {
    if (type === "course") {
      const course = getCoursesDetails.find((c) => c.courseId === value) || null;
      setSelectedCourse(course);
      setSelectedLesson(null);
      setSelectedSubject(null);
    } else if (type === "lesson") {
      const lesson = selectedCourse?.lessons.find((l) => l.lessonId === value) || null;
      setSelectedLesson(lesson);
      setSelectedSubject(null);
    } else if (type === "subject") {
      const subject = selectedLesson?.subjects.find((s) => s.subjectId === value) || null;
      setSelectedSubject(subject);
    }
  };

  // Render paginated items
  const renderPaginatedItems = (items, type, parentId, level, paginationStates) => {
    const subItemsPerPage = 5;
    let paginatedItems = items;
    let totalPages = 1;
    let currentPageForType = 1;

    if (type !== "course") {
      currentPageForType =
        type === "lesson"
          ? paginationStates.lessonPages[parentId] || 1
          : type === "subject"
          ? paginationStates.subjectPages[parentId] || 1
          : paginationStates.videoPages[parentId] || 1;
      const totalItems = items.length;
      totalPages = Math.ceil(totalItems / subItemsPerPage);
      const startIndex = (currentPageForType - 1) * subItemsPerPage;
      const endIndex = startIndex + subItemsPerPage;
      paginatedItems = items.slice(startIndex, endIndex);
    }

    return (
      <div className={`acd-tile-container acd-level-${level}`}>
        {paginatedItems.map((item, index) => {
          if (type === "course") {
            const videoCount = getCourseVideoCount(item);
            return (
              <div key={item.courseId} className="acd-tile acd-course-tile">
                <div
                  className="acd-tile-header"
                  onClick={() => toggleExpand("course", item.courseId)}
                >
                  <span className="acd-tile-title">
                    {index + 1}. {item.courseTitle}{" "}
                    <span className="acd-video-count">({videoCount} videos)</span>
                  </span>
                  <FontAwesomeIcon
                    icon={expandedCourses[item.courseId] ? faChevronUp : faChevronDown}
                    className="acd-expand-icon"
                  />
                </div>
                {expandedCourses[item.courseId] && (
                  <div className="acd-tile-content">
                    {renderPaginatedItems(
                      item.lessons,
                      "lesson",
                      item.courseId,
                      level + 1,
                      paginationStates
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "lesson") {
            const videoCount = getLessonVideoCount(item);
            return (
              <div key={item.lessonId} className="acd-tile acd-lesson-tile">
                <div
                  className="acd-tile-header"
                  onClick={() => toggleExpand("lesson", item.lessonId)}
                >
                  <span className="acd-tile-title">
                    {item.lessonName} <span className="acd-video-count">({videoCount} videos)</span>
                  </span>
                  <FontAwesomeIcon
                    icon={expandedLessons[item.lessonId] ? faChevronUp : faChevronDown}
                    className="acd-expand-icon"
                  />
                </div>
                {expandedLessons[item.lessonId] && (
                  <div className="acd-tile-content">
                    {renderPaginatedItems(
                      item.subjects,
                      "subject",
                      item.lessonId,
                      level + 1,
                      paginationStates
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "subject") {
            const videoCount = getSubjectVideoCount(item);
            return (
              <div key={item.subjectId} className="acd-tile acd-subject-tile">
                <div
                  className="acd-tile-header"
                  onClick={() => toggleExpand("subject", item.subjectId)}
                >
                  <span className="acd-tile-title">
                    {item.subjectName} <span className="acd-video-count">({videoCount} videos)</span>
                  </span>
                  <FontAwesomeIcon
                    icon={expandedSubjects[item.subjectId] ? faChevronUp : faChevronDown}
                    className="acd-expand-icon"
                  />
                </div>
                {expandedSubjects[item.subjectId] && (
                  <div className="acd-tile-content">
                    {item.videos.length > 0 ? (
                      renderPaginatedItems(
                        item.videos,
                        "video",
                        item.subjectId,
                        level + 1,
                        paginationStates
                      )
                    ) : (
                      <div className="acd-tile acd-no-data-tile">No videos found</div>
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "video") {
            return (
              <div key={item._id} className="acd-tile acd-video-tile">
                <div className="acd-tile-header">
                  <span className="acd-tile-title">{item.name}</span>
                  <div className="acd-video-actions">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="acd-video-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Watch Video
                    </a>
                    <button
                      className="acd-copy-button"
                      onClick={() => handleCopyLink(item._id, item.url)}
                      title="Copy video link"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      {copiedVideoId === item._id ? " Copied!" : " Copy Link"}
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })}
        {totalPages > 1 && type !== "course" && (
          <div className="acd-sub-pagination">
            <button
              onClick={() => handleSubPagination(type, parentId, "prev")}
              disabled={currentPageForType === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span>
              Page {currentPageForType} of {totalPages}
            </span>
            <button
              onClick={() => handleSubPagination(type, parentId, "next")}
              disabled={currentPageForType === totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Prepare pagination states
  const paginationStates = { lessonPages, subjectPages, videoPages };

  return (
    <div className="acd-video-list-main">
      <img className="acd-judicial-logo" src={judicialLogo} alt="Judicial Logo" />
      <h2 className="acd-text-white">Download Videos</h2>

      <div className="acd-controls-container">
        <div className="acd-search-container">
          <FontAwesomeIcon icon={faSearch} className="acd-search-icon" />
          <input
            type="text"
            placeholder="Search videos by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="acd-search-input"
          />
        </div>

        <div className="acd-dropdown-container">
          <select
            value={selectedCourse?.courseId || ""}
            onChange={(e) => handleDropdownChange("course", e.target.value)}
            className="acd-dropdown"
          >
            <option value="">All Courses</option>
            {getCoursesDetails.map((course) => (
              <option key={course.courseId} value={course.courseId}>
                {course.courseTitle}
              </option>
            ))}
          </select>

          <select
            value={selectedLesson?.lessonId || ""}
            onChange={(e) => handleDropdownChange("lesson", e.target.value)}
            className="acd-dropdown"
            disabled={!selectedCourse}
          >
            <option value="">All Lessons</option>
            {selectedCourse?.lessons.map((lesson) => (
              <option key={lesson.lessonId} value={lesson.lessonId}>
                {lesson.lessonName}
              </option>
            ))}
          </select>

          <select
            value={selectedSubject?.subjectId || ""}
            onChange={(e) => handleDropdownChange("subject", e.target.value)}
            className="acd-dropdown"
            disabled={!selectedLesson}
          >
            <option value="">All Subjects</option>
            {selectedLesson?.subjects.map((subject) => (
              <option key={subject.subjectId} value={subject.subjectId}>
                {subject.subjectName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="acd-tile-container">
        {loader ? (
          <div className="acd-spinner-wrapper">
            <Spinner />
          </div>
        ) : filteredCourses.length > 0 ? (
          renderPaginatedItems(filteredCourses, "course", null, 1, paginationStates)
        ) : (
          <div className="acd-tile acd-no-data-tile">
            {searchQuery || selectedCourse
              ? "No videos found matching your criteria"
              : "No courses found"}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCoursesDetails;